import {isLoggedIn} from '@github-ui/client-env'
import {DashboardFeedbackDialog, type FeedbackDialogRef} from '@github-ui/dashboard-feedback-dialog'
import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {Filter, type FilterProps} from '@github-ui/filter'
import {SubmitEvent} from '@github-ui/filter-core'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {NewRepoAgentTaskButton} from '@github-ui/new-repo-agent-task-button/NewRepoAgentTaskButton'
import {noop} from '@github-ui/noop'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {DashboardSidebarCollapsible} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsible'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {isStaff, sendCustomMetric} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {AgentIcon, CommentIcon, GitMergeQueueIcon, MilestoneIcon, TagIcon} from '@primer/octicons-react'
import {ActionList, Button, CounterLabel} from '@primer/react'
import {useId, useRef, useState} from 'react'

import {useRepoPullsSearch, useRepoPullsSidebar} from '../hooks/use-repo-pulls-dashboard'
import {repoPullsDashboardRepositoryToRepository} from '../helpers/repo-pulls-dashboard-proto'
import {StacksNavItem} from '../components/stacks/StacksNavItem'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'

import styles from './RepoPullsDashboardHeader.module.css'

const EMPTY_SAVED_VIEWS: never[] = []
const STAFF_FEEDBACK_URL = 'https://gh.io/pr-dashboard-feedback'
const REPO_PULLS_FEEDBACK_SUBJECT = 'repo_pulls_dashboard_feedback'

export function RepoPullsDashboardHeader({showSidebar}: {showSidebar: boolean}) {
  const {fetchedQuery, navigateToResults, owner, repo} = useRepoPullsSearch()
  const {generateLinkPath, selectedLink, sidebarLinks} = useRepoPullsSidebar()
  const {sendAnalyticsEvent} = useAnalytics()
  const [query, setQuery] = useState(fetchedQuery)
  const [previousFetchedQuery, setPreviousFetchedQuery] = useState(fetchedQuery)
  const providers = useFilterProviders({
    repositoryScope: owner && repo ? `${owner}/${repo}` : undefined,
    suggestPullRequestAuthors: true,
  })
  const selectedHeaderLink = selectedLink?.id === 'all' ? {...selectedLink, name: 'All pull requests'} : selectedLink
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const feedbackReturnFocusRef = useRef<HTMLButtonElement>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const staff = isBrowser && isStaff()

  if (previousFetchedQuery !== fetchedQuery) {
    setPreviousFetchedQuery(fetchedQuery)
    setQuery(fetchedQuery)
  }

  const filterProps: FilterProps = {
    id: 'repo-pulls-dashboard-filter',
    label: 'Search pull requests',
    variant: 'input',
    placeholder: 'Search pull requests',
    filterValue: query,
    providers,
    onChange: setQuery,
    onSubmit: (filterQuery, submitEvent) => {
      navigateToResults({q: filterQuery.raw})
      if (submitEvent === SubmitEvent.ExplicitSubmit) {
        sendAnalyticsEvent('pull-requests.search', 'UPDATE_AND_SUBMIT_QUERY')
        sendCustomMetric({name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_SEARCH_SUBMIT', value: 1})
      }
    },
    settings: {groupAndKeywordSupport: true, showSuggestionsOnFocus: isFeatureEnabled('filter_suggestions_on_focus')},
  }

  return (
    <>
      <SharedHeader
        dashboardType="pull-requests"
        links={sidebarLinks}
        selectedLink={selectedHeaderLink}
        onSelectLink={noop}
        savedViews={EMPTY_SAVED_VIEWS}
        generateLinkPath={generateLinkPath}
        optOutLocked
        showOptionsButton={false}
        showSavedViews={false}
        showSidebarTrigger={showSidebar}
        mobileSidebarTriggerRef={feedbackReturnFocusRef}
        mobileSidebarFooter={({onClose}) => (
          <>
            <RepoPullsDashboardPages />
            <RepoPullsDashboardFeedback
              onGiveFeedback={() => {
                onClose()
                feedbackRef.current?.openDialog()
              }}
            />
          </>
        )}
        primaryAction={<RepoPullsDashboardActions />}
        filter={
          <div role="search" aria-label="Pull requests">
            <Filter {...filterProps} data-testid="repo-pulls-dashboard-filter" />
          </div>
        }
      />
      {showSidebar && !staff && (
        <DashboardFeedbackDialog
          ref={feedbackRef}
          subject={REPO_PULLS_FEEDBACK_SUBJECT}
          placeholderText="Tell us your feedback on the new pull request experience"
          hideButton
          returnFocusRef={feedbackReturnFocusRef}
        />
      )}
    </>
  )
}

export function RepoPullsDashboardCollapsibleSidebar() {
  const {generateLinkPath, selectedLink, sidebarLinks} = useRepoPullsSidebar()

  return (
    <>
      <DashboardSidebarCollapsible
        dashboardType="pull-requests"
        links={sidebarLinks}
        selectedLink={selectedLink?.id || ''}
        onSelectLink={noop}
        savedViews={EMPTY_SAVED_VIEWS}
        generateLinkPath={generateLinkPath}
      />
      <RepoPullsDashboardPages />
    </>
  )
}

function RepoPullsDashboardPages() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {labels, mergeQueueUrl, milestones, stacks} = layoutPayload?.actions ?? {}
  const showMergeQueue = !!mergeQueueUrl && isFeatureEnabled('repo_pulls_dashboard_merge_queue_link')

  if (!labels && !showMergeQueue && !milestones && !stacks) return null

  return (
    <>
      <hr className={styles.SidebarDivider} />
      <CollapsibleNavList aria-label="Pages">
        {milestones ? (
          <CollapsibleNavList.LinkItem to={milestones.url} icon={MilestoneIcon} label="Milestones" />
        ) : null}
        {labels ? <CollapsibleNavList.LinkItem to={labels.url} icon={TagIcon} label="Labels" /> : null}
        {stacks ? <StacksNavItem url={stacks.url} /> : null}
        {showMergeQueue ? (
          <CollapsibleNavList.LinkItem to={mergeQueueUrl} icon={GitMergeQueueIcon} label="Merge queue" />
        ) : null}
      </CollapsibleNavList>
    </>
  )
}

export function RepoPullsDashboardFeedback({onGiveFeedback}: {onGiveFeedback?: () => void}) {
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const triggerRef = useRef<HTMLLIElement>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const staff = isBrowser && isStaff()
  if (!isLoggedIn()) return null

  return (
    <>
      <SidebarTooltip text="Feedback" disabled={visualIsExpanded}>
        <ActionList className={styles.FeedbackLinkWrapper}>
          {staff ? (
            <ActionList.LinkItem href={STAFF_FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Feedback
            </ActionList.LinkItem>
          ) : (
            <ActionList.Item ref={triggerRef} onSelect={onGiveFeedback ?? (() => feedbackRef.current?.openDialog())}>
              <ActionList.LeadingVisual>
                <CommentIcon />
              </ActionList.LeadingVisual>
              Feedback
            </ActionList.Item>
          )}
        </ActionList>
      </SidebarTooltip>
      {!staff && !onGiveFeedback && (
        <DashboardFeedbackDialog
          ref={feedbackRef}
          subject={REPO_PULLS_FEEDBACK_SUBJECT}
          placeholderText="Tell us your feedback on the new pull request experience"
          hideButton
          returnFocusRef={triggerRef}
        />
      )}
    </>
  )
}

function RepoPullsDashboardActions() {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const actions = layoutPayload?.actions
  const disabledReasonId = useId()
  if (!actions) return null

  const {agentTaskRepository, labels, milestones, newPullRequest} = actions
  const showHeaderPages = !isLoggedIn()
  return (
    <div className="d-flex flex-wrap gap-2">
      {showHeaderPages && labels ? (
        <Button as={Link} to={labels.url} leadingVisual={TagIcon}>
          Labels <CounterLabel>{labels.count}</CounterLabel>
        </Button>
      ) : null}
      {showHeaderPages && milestones ? (
        <Button as={Link} to={milestones.url} leadingVisual={MilestoneIcon}>
          Milestones <CounterLabel>{milestones.count}</CounterLabel>
        </Button>
      ) : null}
      {agentTaskRepository ? (
        <NewRepoAgentTaskButton
          repository={repoPullsDashboardRepositoryToRepository(agentTaskRepository)}
          icon={AgentIcon}
          label="New agent session"
          eventType="new_agent_task_button_repo_pulls"
          newSessionComposer={isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')}
        />
      ) : null}
      {newPullRequest?.url ? (
        <Button as={Link} to={newPullRequest.url} variant="primary">
          {newPullRequest.label}
        </Button>
      ) : newPullRequest?.disabledReason ? (
        <>
          <Button
            variant="primary"
            inactive
            aria-disabled="true"
            aria-describedby={disabledReasonId}
            title={newPullRequest.disabledReason}
          >
            {newPullRequest.label}
          </Button>
          <span id={disabledReasonId} className="sr-only">
            {newPullRequest.disabledReason}
          </span>
        </>
      ) : null}
    </div>
  )
}
